import { useState } from 'react';
import type { Deal, SCData, TabState, Touchpoint } from '../../types';
import { buildSCList, buildTouchpointSCList, PRODUCT_BUCKETS, bucketProducts, matchesCQFilter } from '../../utils/filter';
import { fmtG } from '../../utils/format';
import { sumUniqueBy, validAges, isAged, ageSeverity, formatAge, splitSCNames } from '../../utils/deals';
import { parseWfDate } from '../../utils/date';
import { useDeals } from '../../context/DealsContext';
import { MetricCard } from '../ui/MetricCard';
import { MedianAgeCard } from '../ui/MedianAgeCard';
import { GnarrInfluencedCard } from '../ui/GnarrInfluencedCard';
import { SectionHeader } from '../ui/SectionHeader';
import { SortableTable, type SortableColumn } from '../ui/SortableTable';
import { getURL } from '../../utils/deals';
interface Props {
  state: TabState;
}
type DealViewMode = 'cards' | 'table' | 'tile' | 'gallery' | 'waterfall';

/* ─── SVG icons for the view switcher ──────────────────────────────────── */

function IconCards() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-259" data-mp="3m00mhcyklgdm">
      <rect x="1" y="1" width="6" height="6" rx="1" data-id="element-260" data-mp="3h1yug2j626q0" />
      <rect x="9" y="1" width="6" height="6" rx="1" data-id="element-261" data-mp="3vrehp8jhnu0y" />
      <rect x="1" y="9" width="6" height="6" rx="1" data-id="element-262" data-mp="3vt7rgwpeeud6" />
      <rect x="9" y="9" width="6" height="6" rx="1" data-id="element-263" data-mp="3qt37uxt71jiq" />
    </svg>;
}
function IconTable() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-264" data-mp="3338umf4ypsxy">
      <rect x="1" y="2" width="14" height="12" rx="1.5" data-id="element-265" data-mp="2y4o3d57oujln" />
      <line x1="1" y1="5.5" x2="15" y2="5.5" data-id="element-266" data-mp="3c2vzjawwhgvr" />
      <line x1="1" y1="9" x2="15" y2="9" data-id="element-267" data-mp="3c4p6krbfe34a" />
      <line x1="6" y1="5.5" x2="6" y2="14" data-id="element-268" data-mp="381k5mt6zu9hr" />
    </svg>;
}
function IconTile() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-269" data-mp="rcwsr4rldduq">
      <rect x="1" y="1" width="4" height="4" rx="0.5" data-id="element-270" data-mp="nc358ci2a12d" />
      <rect x="6" y="1" width="4" height="4" rx="0.5" data-id="element-271" data-mp="ndebciyzbkwn" />
      <rect x="11" y="1" width="4" height="4" rx="0.5" data-id="element-272" data-mp="3g4qmrvs4dnoy" />
      <rect x="1" y="6" width="4" height="4" rx="0.5" data-id="element-273" data-mp="3l4wf7e67re83" />
      <rect x="6" y="6" width="4" height="4" rx="0.5" data-id="element-274" data-mp="3l3blfqrq3zba" />
      <rect x="11" y="6" width="4" height="4" rx="0.5" data-id="element-275" data-mp="3q495k8wan3ff" />
      <rect x="1" y="11" width="4" height="4" rx="0.5" data-id="element-276" data-mp="3q4r5bfi5zzs3" />
      <rect x="6" y="11" width="4" height="4" rx="0.5" data-id="element-277" data-mp="3u5uoobzf8dy2" />
      <rect x="11" y="11" width="4" height="4" rx="0.5" data-id="element-278" data-mp="3u38h4ccj5yfu" />
    </svg>;
}
function IconGallery() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-279" data-mp="32eesbq82bcun">
      <rect x="1" y="1" width="6" height="7" rx="1" data-id="element-280" data-mp="36fqipxyrg23w" />
      <rect x="9" y="1" width="6" height="7" rx="1" data-id="element-281" data-mp="36dw0irevio3i" />
      <rect x="1" y="10" width="14" height="5" rx="1" data-id="element-282" data-mp="3b7qpa99c5t9g" />
    </svg>;
}
function IconWaterfall() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-283" data-mp="2eo19mqbdaqcq">
      <line x1="2" y1="1" x2="2" y2="15" data-id="element-284" data-mp="2iobbprw1gwmb" />
      <line x1="6" y1="1" x2="6" y2="15" data-id="element-285" data-mp="2b7es2xzj8ckv" />
      <line x1="10" y1="1" x2="10" y2="15" data-id="element-286" data-mp="2b63r15xh1ixg" />
      <line x1="14" y1="1" x2="14" y2="15" data-id="element-287" data-mp="268205ytt0fbf" />
      <rect x="1" y="3" width="3" height="2" rx="0.5" fill="currentColor" opacity="0.3" data-id="element-288" data-mp="268bn1djjy6cw" />
      <rect x="5" y="6" width="3" height="3" rx="0.5" fill="currentColor" opacity="0.3" data-id="element-289" data-mp="224vsqnuygi3d" />
      <rect x="9" y="2" width="3" height="4" rx="0.5" fill="currentColor" opacity="0.3" data-id="element-290" data-mp="1x62h70z8b15u" />
      <rect x="13" y="8" width="2" height="3" rx="0.5" fill="currentColor" opacity="0.3" data-id="element-291" data-mp="1s8am5y3aeyxf" />
    </svg>;
}
const VIEW_OPTIONS: {
  mode: DealViewMode;
  label: string;
  icon: React.FC;
}[] = [{
  mode: 'cards',
  label: 'Cards',
  icon: IconCards
}, {
  mode: 'table',
  label: 'Table',
  icon: IconTable
}, {
  mode: 'tile',
  label: 'Tile',
  icon: IconTile
}, {
  mode: 'gallery',
  label: 'Gallery',
  icon: IconGallery
}, {
  mode: 'waterfall',
  label: 'Waterfall',
  icon: IconWaterfall
}];

/* ─── Shared request-name link with icon ───────────────────────────────── */

const LINK_STYLE: React.CSSProperties = {
  color: '#1d4ed8',
  textDecoration: 'none'
};
function RequestLink({
  d,
  maxLen = 35
}: {
  d: Deal;
  maxLen?: number;
}) {
  return <a href={getURL(d)} target="_blank" rel="noopener noreferrer" style={LINK_STYLE} title={d.requestName} data-id="element-292" data-mp="39llp5n9tf946">
      <svg viewBox="0 0 12 12" fill="none" stroke="#1d4ed8" strokeWidth="1.4" strokeLinecap="round" style={{
      display: 'inline-block',
      width: 9,
      height: 9,
      marginRight: 3,
      verticalAlign: 'middle',
      position: 'relative',
      top: -1
    }} data-id="element-293" data-mp="2ztpqob3thgzf">
        <path d="M5 1.5H3.5A2 2 0 0 0 1.5 3.5v5A2 2 0 0 0 3.5 10.5h5a2 2 0 0 0 2-2V8.5" data-id="element-294" data-mp="2pwl1e2eod5ee" />
        <path d="M7.5 1.5h3v3" data-id="element-295" data-mp="2bx26ibavjqk1" />
        <path d="M5.5 6.5 10.5 1.5" data-id="element-296" data-mp="2by4x0i4abqzf" />
      </svg>
      {d.requestName.substring(0, maxLen)}
    </a>;
}

/* ─── View switcher bar ────────────────────────────────────────────────── */

function ViewSwitcher({
  active,
  onChange
}: {
  active: DealViewMode;
  onChange: (m: DealViewMode) => void;
}) {
  return <div className="view-switcher" data-id="element-297" data-mp="f1nunn56jouq">
      {VIEW_OPTIONS.map(opt => <button key={opt.mode} className={active === opt.mode ? 'active' : ''} onClick={() => onChange(opt.mode)} title={opt.label} data-id="element-298" data-mp="1zmauzqolzl8f">
          <opt.icon data-id="element-299" data-mp="29cnnq66kukpn" />
          {opt.label}
        </button>)}
    </div>;
}

/* ─── Age bucket helper for waterfall ──────────────────────────────────── */

function ageBucket(age: number | null): string {
  if (age == null) return 'Unknown';
  if (age <= 30) return '0–30 days';
  if (age <= 60) return '31–60 days';
  if (age <= 90) return '61–90 days';
  return '90+ days';
}
const AGE_BUCKETS = ['0–30 days', '31–60 days', '61–90 days', '90+ days'];
function groupByAgeBucket(deals: Deal[]): Record<string, Deal[]> {
  const groups: Record<string, Deal[]> = {};
  for (const b of AGE_BUCKETS) groups[b] = [];
  for (const d of deals) {
    const b = ageBucket(d.age);
    if (!groups[b]) groups[b] = [];
    groups[b].push(d);
  }
  return groups;
}

/* ─── Display-only age for in-progress deals ──────────────────────────── */

function displayAge(d: Deal): number | null {
  if (d.age !== null) return d.age;
  if (d.status !== 'In Progress' || !d.entry) return null;
  const entryDate = parseWfDate(d.entry);
  if (!entryDate) return null;
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  return Math.round((Date.now() - entryDate.getTime()) / MS_PER_DAY);
}

/* ─── Individual view renderers ────────────────────────────────────────── */

function TableView({
  deals
}: {
  deals: Deal[];
}) {
  return <table className="deal-table" data-id="element-300" data-mp="25keawwmro7dn">
      <thead data-id="element-301" data-mp="25jl4jwrpzjw1">
        <tr data-id="element-302" data-mp="2acxq1y13rakk">
          <th style={{
          width: '9%'
        }} data-id="element-303" data-mp="2ace5sanzca3d">ID</th>
          <th style={{
          width: '16%'
        }} data-id="element-304" data-mp="2adfrra6jagzv">Account</th>
          <th style={{
          width: '18%'
        }} data-id="element-305" data-mp="2fcadokzsslo7">Request</th>
          <th style={{
          width: '8%'
        }} data-id="element-306" data-mp="2fdv5i2uq9qid">Age</th>
          <th style={{
          width: '8%'
        }} data-id="element-307" data-mp="2kbdf7vgkk9rf">Date</th>
          <th style={{
          width: '9%'
        }} data-id="element-308" data-mp="2kcoilu63rfi0">GNARR</th>
          <th style={{
          width: '9%'
        }} data-id="element-309" data-mp="2p9f10o5dqn6l">Close Q</th>
          <th style={{
          width: '13%'
        }} data-id="element-310" data-mp="2pa6ikv256kdv">Offering</th>
          <th style={{
          width: '10%'
        }} data-id="element-311" data-mp="dn4i3tdglhnf">Sub Region</th>
        </tr>
      </thead>
      <tbody data-id="element-312" data-mp="8ni4vjxms1em">
        {deals.map(d => <tr key={d.requestID} data-id="element-313" data-mp="8mp4d0p9m84x">
            <td style={{
          color: '#8892a0',
          fontSize: 9
        }} data-id="element-314" data-mp="8pl32r8xt0uf">{d.id}</td>
            <td title={d.account} data-id="element-315" data-mp="3p6t33nrzgtr">{d.account.substring(0, 24)}</td>
            <td title={d.requestName} data-id="element-316" data-mp="3uv863hrawki1">
              <RequestLink d={d} maxLen={30} data-id="element-317" data-mp="3uw9jvtzpbl3g" />
            </td>
            <td className={ageSeverity(displayAge(d)) === 'high' ? 'c-red' : ageSeverity(displayAge(d)) === 'medium' ? 'c-gold' : ''} style={{
          fontWeight: ageSeverity(displayAge(d)) === 'high' ? 600 : 400
        }} data-id="element-318" data-mp="wdcdfpax33t2">
              {formatAge(displayAge(d))}
            </td>
            <td style={{
          fontSize: 9,
          color: '#667085'
        }} data-id="element-319" data-mp="mkwyl0ux2vce">{d.entry}</td>
            <td data-id="element-320" data-mp="hnwomeuy58g0">{fmtG(d.gnarr)}</td>
            <td data-id="element-321" data-mp="1f3kf02yyxm75">{d.close_q}</td>
            <td data-id="element-322" data-mp="1f4lzsz5sdn22"><span className="badge b-blue" style={{
            fontSize: 8
          }} data-id="element-323" data-mp="1f4ly8uq51ezy">{d.offering.substring(0, 18)}</span></td>
            <td data-id="element-324" data-mp="1ac2c39ddxbjj"><span className="badge b-gray" style={{
            fontSize: 8
          }} data-id="element-325" data-mp="1ac2dndt19jln">{d.sub_region}</span></td>
          </tr>)}
      </tbody>
    </table>;
}
function TileView({
  deals
}: {
  deals: Deal[];
}) {
  return <div className="deal-tile-grid" data-id="element-326" data-mp="nhkdahlvyadw">
      {deals.map(d => <div key={d.requestID} className="deal-tile" title={`${d.account}\n${d.requestName}\nAge: ${formatAge(displayAge(d))}\nGNARR: ${fmtG(d.gnarr)}\nDate: ${d.entry}`} data-id="element-327" data-mp="9pdvys6zxxcv">
          <div className="dt-id" data-id="element-328" data-mp="12d9xqbbfafgi">{d.id}</div>
          <div className="dt-acct" data-id="element-329" data-mp="12el2oegltt97">{d.account.substring(0, 14)}</div>
          <div style={{
        fontSize: 8,
        marginTop: 2,
        lineHeight: 1.2
      }} data-id="element-330" data-mp="16fdcp9xaw26j">
            <RequestLink d={d} maxLen={16} data-id="element-331" data-mp="16citflq0wnep" />
          </div>
          <div style={{
        fontSize: 8,
        color: '#667085',
        marginTop: 2
      }} data-id="element-332" data-mp="seasl2pv91y9">{d.entry}</div>
          <span className={`dt-age ${ageSeverity(displayAge(d)) === 'high' ? 'b-red' : ageSeverity(displayAge(d)) === 'medium' ? 'b-amber' : 'b-gray'}`} data-id="element-333" data-mp="xd45ou18qqxw">
            {formatAge(displayAge(d))}
          </span>
        </div>)}
    </div>;
}
function GalleryView({
  deals
}: {
  deals: Deal[];
}) {
  return <div className="deal-gallery" data-id="element-334" data-mp="3ajkbjexcog22">
      {deals.map(d => <div key={d.requestID} className="deal-gallery-card" data-id="element-335" data-mp="35mbop119bnuh">
          <div className="dg-id" data-id="element-336" data-mp="35nwi2nbu50qr">{d.id}</div>
          <div className="dg-title" data-id="element-337" data-mp="3jltijutetrap">{d.account}</div>
          <div style={{
        fontSize: 10,
        marginBottom: 4
      }} data-id="element-338" data-mp="3jjh0zirkdiy6">
            <RequestLink d={d} maxLen={40} data-id="element-339" data-mp="3enjo5mfieuq5" />
          </div>
          <div style={{
        fontSize: 9,
        color: '#667085',
        marginBottom: 4
      }} data-id="element-340" data-mp="2qww27x8re4kl">{d.entry}</div>
          <div className="dg-meta" data-id="element-341" data-mp="2qujibejfxk4w">
            <span className={`badge ${ageSeverity(displayAge(d)) === 'high' ? 'b-red' : ageSeverity(displayAge(d)) === 'medium' ? 'b-amber' : 'b-gray'}`} data-id="element-342" data-mp="2mw3rezk8nrm7">{formatAge(displayAge(d))}</span>
            <span className="badge b-blue" data-id="element-343" data-mp="2vvh21vk3mm8i">{fmtG(d.gnarr)}</span>
            <span className="badge b-purple" style={{
          fontSize: 8
        }} data-id="element-344" data-mp="2vtw9gbhchdda">{d.close_q}</span>
            {isAged(d) && <span className="badge b-red" data-id="element-345" data-mp="2841unodgteqe">⚠ aged</span>}
          </div>
          <div className="dg-footer" data-id="element-346" data-mp="236s8chldhj6s">
            {d.sub_region} · {d.offering.substring(0, 26)}
          </div>
        </div>)}
    </div>;
}
function WaterfallView({
  deals
}: {
  deals: Deal[];
}) {
  const groups = groupByAgeBucket(deals);
  return <div className="deal-waterfall" data-id="element-347" data-mp="3vt4jled8v4d3">
      {AGE_BUCKETS.map(bucket => {
      const items = groups[bucket] || [];
      return <div key={bucket} className="deal-waterfall-lane" data-id="element-348" data-mp="3rrj03a67frtx">
            <div className="deal-waterfall-lane-header" data-id="element-349" data-mp="3mthiyusca2l3">
              <span data-id="element-350" data-mp="3mv2b6dr6l9fs">{bucket}</span>
              <span className="lane-count" data-id="element-351" data-mp="3i2ivnb8xo48c">{items.length}</span>
            </div>
            <div className="deal-waterfall-lane-body" data-id="element-352" data-mp="3d360m37wrquo">
              {items.length === 0 && <div style={{
            fontSize: 9,
            color: '#d1d5db',
            textAlign: 'center',
            padding: '12px 4px',
            fontStyle: 'italic'
          }} data-id="element-353" data-mp="3d0tpa8wnoeql">none</div>}
              {items.map(d => <div key={d.requestID} className="deal-waterfall-card" data-id="element-354" data-mp="344kkeepwsnx2">
                  <div className="dw-id" data-id="element-355" data-mp="2z4py6oh2wmic">{d.id}</div>
                  <div className="dw-acct" title={d.account} data-id="element-356" data-mp="1elmdqafe8z5w">{d.account.substring(0, 18)}</div>
                  <div style={{
              fontSize: 8,
              marginTop: 2,
              lineHeight: 1.2
            }} data-id="element-357" data-mp="1jkfk7iw9jpwk">
                    <RequestLink d={d} maxLen={20} data-id="element-358" data-mp="1jma3ytvstbz2" />
                  </div>
                  <div style={{
              fontSize: 8,
              color: '#667085',
              marginTop: 2
            }} data-id="element-359" data-mp="1nmk8e23xzubt">{d.entry}</div>
                  <div className="dw-tags" data-id="element-360" data-mp="1sklruo5a5vnt">
                    <span className={`badge ${ageSeverity(displayAge(d)) === 'high' ? 'b-red' : ageSeverity(displayAge(d)) === 'medium' ? 'b-amber' : 'b-gray'}`} style={{
                fontSize: 8
              }} data-id="element-361" data-mp="vuuu8g1ozsds">{formatAge(displayAge(d))}</span>
                    <span className="badge b-blue" style={{
                fontSize: 8
              }} data-id="element-362" data-mp="14v1a28kv54g4">{fmtG(d.gnarr)}</span>
                  </div>
                </div>)}
            </div>
          </div>;
    })}
    </div>;
}

/* ─── Dispatch view ────────────────────────────────────────────────────── */

function DealExpandedView({
  deals,
  mode
}: {
  deals: Deal[];
  mode: DealViewMode;
}) {
  const [dSearchId, setDSearchId] = useState('');
  const [dSearchAccount, setDSearchAccount] = useState('');
  const [dSearchRequest, setDSearchRequest] = useState('');
  const [dAgeMin, setDAgeMin] = useState('');
  const [dAgeMax, setDAgeMax] = useState('');
  const [dEntryFrom, setDEntryFrom] = useState('');
  const [dEntryTo, setDEntryTo] = useState('');
  const [dGnarrMin, setDGnarrMin] = useState('');
  const [dGnarrMax, setDGnarrMax] = useState('');
  const [dCloseQFilter, setDCloseQFilter] = useState<string[]>([]);
  const [dCloseQDropdownOpen, setDCloseQDropdownOpen] = useState(false);
  const [dOfferingFilter, setDOfferingFilter] = useState<string[]>([]);
  const [dOfferingDropdownOpen, setDOfferingDropdownOpen] = useState(false);
  const [dSubFilter, setDSubFilter] = useState<string[]>([]);
  const [dSubDropdownOpen, setDSubDropdownOpen] = useState(false);
  const dCloseQs = [...new Set(deals.map(d => d.close_q).filter(Boolean))].sort();
  const dOfferings = [...new Set(deals.map(d => d.offering).filter(Boolean))].sort();
  const dSubs = [...new Set(deals.map(d => d.sub_region).filter(Boolean))].sort();
  const parseEntry = (entry: string): Date | null => {
    if (!entry) return null;
    const p = entry.split('/');
    if (p.length !== 3) return null;
    const [dd, mm, yy] = p.map(Number);
    return new Date(yy > 50 ? 1900 + yy : 2000 + yy, mm - 1, dd);
  };
  const filtered = deals.filter(d => {
    if (dSearchId && !d.id.toLowerCase().includes(dSearchId.toLowerCase())) return false;
    if (dSearchAccount && !d.account.toLowerCase().includes(dSearchAccount.toLowerCase())) return false;
    if (dSearchRequest && !d.requestName.toLowerCase().includes(dSearchRequest.toLowerCase())) return false;
    if (dAgeMin || dAgeMax) {
      const age = displayAge(d);
      if (age === null) return false;
      if (dAgeMin && age < Number(dAgeMin)) return false;
      if (dAgeMax && age > Number(dAgeMax)) return false;
    }
    if (dEntryFrom || dEntryTo) {
      const dt = parseEntry(d.entry);
      if (dt) {
        if (dEntryFrom) {
          const f = new Date(dEntryFrom);
          if (dt < f) return false;
        }
        if (dEntryTo) {
          const t = new Date(dEntryTo);
          if (dt > t) return false;
        }
      }
    }
    if (dGnarrMin && d.gnarr < Number(dGnarrMin)) return false;
    if (dGnarrMax && d.gnarr > Number(dGnarrMax)) return false;
    if (dCloseQFilter.length > 0 && !dCloseQFilter.includes(d.close_q)) return false;
    if (dOfferingFilter.length > 0 && !dOfferingFilter.includes(d.offering)) return false;
    if (dSubFilter.length > 0 && !dSubFilter.includes(d.sub_region)) return false;
    return true;
  });
  if (deals.length === 0) {
    return <div style={{
      fontSize: 10,
      color: '#8892a0',
      padding: '8px 0',
      fontStyle: 'italic'
    }} data-id="element-363" data-mp="2h5velukq69jl">No in-progress deals.</div>;
  }
  const inputStyle = {
    fontSize: 10,
    padding: '4px 8px',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    outline: 'none',
    width: 110,
    color: '#344054',
    background: '#fff'
  };
  const dateStyle = {
    fontSize: 10,
    padding: '3px 6px',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    outline: 'none',
    color: '#344054',
    background: '#fff'
  };
  const numStyle = {
    fontSize: 10,
    padding: '4px 6px',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    outline: 'none',
    width: 60,
    color: '#344054',
    background: '#fff'
  };
  const btnStyle = {
    fontSize: 10,
    padding: '4px 8px',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    outline: 'none',
    color: '#344054',
    background: '#fff',
    cursor: 'pointer' as const,
    display: 'flex' as const,
    alignItems: 'center' as const,
    gap: 4
  };
  const ddStyle = {
    position: 'absolute' as const,
    top: '100%',
    left: 0,
    marginTop: 4,
    zIndex: 50,
    background: '#fff',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    boxShadow: '0 2px 6px rgba(0,0,0,.12)',
    minWidth: 140,
    padding: '4px 0'
  };
  const renderDropdown = (items: string[], selected: string[], toggle: (v: string[]) => void, open: boolean, setOpen: (v: boolean) => void) => <div style={{
    position: 'relative'
  }} data-id="element-364" data-mp="msj6p78v9jfu">
      <button style={btnStyle} onClick={() => setOpen(!open)} data-id="element-365" data-mp="2jsduba1wxrwu">
        {selected.length > 0 ? `${selected.length} selected` : `${items[0] === dCloseQs[0] ? 'Close Q' : items[0] === dOfferings[0] ? 'Offering' : 'Sub'} ▾`}
      </button>
      {open && <div style={ddStyle} data-id="element-366" data-mp="2sm8ppqd8g3za">
          <div onClick={() => toggle(selected.length === items.length ? [] : [...items])} style={{
        padding: '4px 10px',
        fontSize: 10,
        cursor: 'pointer',
        color: '#667085',
        borderBottom: '1px solid #eaecf0'
      }} data-id="element-367" data-mp="2nu8uucc0s19k">
            {selected.length === items.length ? 'Deselect all' : 'Select all'}
          </div>
          {items.map(item => <label key={item} style={{
        padding: '4px 10px',
        fontSize: 10,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 6
      }} onClick={e => e.stopPropagation()} data-id="element-368" data-mp="3biid54a0y6vy">
              <input type="checkbox" checked={selected.includes(item)} onChange={() => toggle(selected.includes(item) ? selected.filter(x => x !== item) : [...selected, item])} data-id="element-369" data-mp="1h6tt4noxre4p" />
              {item}
            </label>)}
        </div>}
    </div>;
  const toComparable = (entry: string) => {
    if (!entry) return 0;
    const [dd, mm, yy] = entry.split('/');
    return +yy * 10000 + +mm * 100 + +dd;
  };
  const sorted = [...filtered].sort((a, b) => toComparable(b.entry) - toComparable(a.entry));
  const filterBar = <div style={{
    padding: '6px 0',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap'
  }} data-id="element-370" data-mp="jcj4bnu9cslx">
      <input type="text" placeholder="ID" value={dSearchId} onChange={e => setDSearchId(e.target.value)} style={inputStyle} data-id="element-371" data-mp="ob41tfi4ky6o" />
      <input type="text" placeholder="Account" value={dSearchAccount} onChange={e => setDSearchAccount(e.target.value)} style={inputStyle} data-id="element-372" data-mp="acmkbzitchx7" />
      <input type="text" placeholder="Request" value={dSearchRequest} onChange={e => setDSearchRequest(e.target.value)} style={{
      ...inputStyle,
      width: 130
    }} data-id="element-373" data-mp="fcs7b2e51g8y" />
      <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 3,
      fontSize: 10,
      color: '#667085'
    }} data-id="element-374" data-mp="29nnminjtvtkp">
        <span data-id="element-375" data-mp="3i7t88d7wmque">Age</span>
        <input type="number" placeholder="min" value={dAgeMin} onChange={e => setDAgeMin(e.target.value)} style={numStyle} data-id="element-376" data-mp="3i8colphwp76b" />
        <span data-id="element-377" data-mp="38jbciaehf9xm">–</span>
        <input type="number" placeholder="max" value={dAgeMax} onChange={e => setDAgeMax(e.target.value)} style={numStyle} data-id="element-378" data-mp="38it4kgigj79v" />
      </div>
      <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 3,
      fontSize: 10,
      color: '#667085'
    }} data-id="element-379" data-mp="34h7ybe0fhr8n">
        <span data-id="element-380" data-mp="2zk7r2zrueify">Entry</span>
        <input type="date" value={dEntryFrom} onChange={e => setDEntryFrom(e.target.value)} style={dateStyle} data-id="element-381" data-mp="2zga76bxfxmv0" />
        <span data-id="element-382" data-mp="2pkgyj3muedii">–</span>
        <input type="date" value={dEntryTo} onChange={e => setDEntryTo(e.target.value)} style={dateStyle} data-id="element-383" data-mp="2pjoztjxy7zo0" />
      </div>
      <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 3,
      fontSize: 10,
      color: '#667085'
    }} data-id="element-384" data-mp="2lk6rror87j2l">
        <span data-id="element-385" data-mp="nrsrej72v5xg">GNARR</span>
        <input type="number" placeholder="min" value={dGnarrMin} onChange={e => setDGnarrMin(e.target.value)} style={numStyle} data-id="element-386" data-mp="nqzra12mjeoa" />
        <span data-id="element-387" data-mp="21pdiednyeygi">–</span>
        <input type="number" placeholder="max" value={dGnarrMax} onChange={e => setDGnarrMax(e.target.value)} style={numStyle} data-id="element-388" data-mp="21px0budltmuj" />
      </div>
      {renderDropdown(dCloseQs, dCloseQFilter, setDCloseQFilter, dCloseQDropdownOpen, setDCloseQDropdownOpen)}
      {renderDropdown(dOfferings, dOfferingFilter, setDOfferingFilter, dOfferingDropdownOpen, setDOfferingDropdownOpen)}
      {renderDropdown(dSubs, dSubFilter, setDSubFilter, dSubDropdownOpen, setDSubDropdownOpen)}
    </div>;
  return <>
      {filterBar}
      <div style={{
      fontSize: 9,
      color: '#8892a0',
      marginBottom: 4
    }} data-id="element-389" data-mp="1nq5h9f88dg9o">{filtered.length} of {deals.length} deals</div>
      <DealExpandedViewInner deals={sorted} mode={mode} data-id="element-390" data-mp="1rpmooe77wnhh" />
    </>;
}
function DealExpandedViewInner({
  deals,
  mode
}: {
  deals: Deal[];
  mode: DealViewMode;
}) {
  switch (mode) {
    case 'table':
      return <TableView deals={deals} data-id="element-391" data-mp="335uq2cawxsrk" />;
    case 'tile':
      return <TileView deals={deals} data-id="element-392" data-mp="384egoq0gbsq9" />;
    case 'gallery':
      return <GalleryView deals={deals} data-id="element-393" data-mp="3870pesz2wea2" />;
    case 'waterfall':
      return <WaterfallView deals={deals} data-id="element-394" data-mp="39l5htarcrczx" />;
    case 'cards':
    default:
      return <div className="deal-grid" data-id="element-395" data-mp="3ejyig2ljj9io">
          {deals.map(d => <div key={d.requestID} className="deal-chip" data-id="element-396" data-mp="3jbqf8uvaibh9">
              <div className="dc-id" data-id="element-397" data-mp="3jc8e7z9c73sv">{d.id}</div>
              <div className="dc-name" data-id="element-398" data-mp="3oajo29zmteb3">{d.account.substring(0, 35)}</div>
              <div style={{
            fontSize: 9,
            marginTop: 3
          }} data-id="element-399" data-mp="3ocntiwd6ildh"><RequestLink d={d} data-id="element-400" data-mp="2qnuk3uldcrml" /></div>
              <div style={{
            fontSize: 8,
            color: '#667085',
            marginTop: 2
          }} data-id="element-401" data-mp="2qovwq3i19m6f">{d.entry}</div>
              <div className="dc-tags" data-id="element-402" data-mp="2vmntbatmhwgy">
            <span className={`badge ${ageSeverity(displayAge(d)) === 'high' ? 'b-red' : ageSeverity(displayAge(d)) === 'medium' ? 'b-amber' : 'b-gray'}`} data-id="element-403" data-mp="30l8mwreddhwf">{formatAge(displayAge(d))}</span>
                <span className="badge b-blue" data-id="element-404" data-mp="35hz45i1wujjf">{fmtG(d.gnarr)}</span>
                <span className="badge b-purple" style={{
              fontSize: 8
            }} data-id="element-405" data-mp="fuhzqho1bnpp">{d.close_q}</span>
                {isAged(d) && <span className="badge b-red" data-id="element-406" data-mp="ks9ox3xaovq7">⚠ aged</span>}
              </div>
              <div style={{
            fontSize: 9,
            color: '#8892a0',
            marginTop: 3
          }} data-id="element-407" data-mp="25atyumbnswyn">
                {d.sub_region} · {d.offering.substring(0, 26)}
              </div>
            </div>)}
        </div>;
  }
}

/* ─── SC Matrix (unchanged) ───────────────────────────────────────────── */

function SCMatrix({
  region,
  sub,
  cqState,
  data
}: {
  region: string;
  sub: string;
  cqState: TabState;
  data: Deal[];
}) {
  const ipData = data.filter(d => d.status === 'In Progress' && (region === 'Global' || d.region === region) && (!sub || sub === 'All' || d.sub_region === sub) && matchesCQFilter(d, cqState));
  const COLS = [...PRODUCT_BUCKETS.map(b => b.label), 'Other'];
  const scMap: Record<string, Record<string, number>> = {};
  const seenDR: Record<string, number> = {};
  for (const d of ipData) {
    const bkts = bucketProducts(d.products || '');
    for (const scName of splitSCNames(d.sc)) {
      if (!scMap[scName]) scMap[scName] = {};
      for (const bkt of bkts) {
        const uniq = scName + '|' + bkt + '|' + d.id;
        if (seenDR[uniq]) continue;
        seenDR[uniq] = 1;
        scMap[scName][bkt] = (scMap[scName][bkt] || 0) + 1;
      }
    }
  }
  const scRows = Object.entries(scMap).map(([name, bkts]) => ({
    name: name.split(' ')[0],
    full: name,
    bkts,
    total: Object.values(bkts).reduce((a, b) => a + b, 0)
  })).sort((a, b) => b.total - a.total);
  const colTotals: Record<string, number> = {};
  for (const c of COLS) colTotals[c] = 0;
  for (const row of scRows) {
    for (const c of COLS) colTotals[c] += row.bkts[c] || 0;
  }
  const grandTotal = Object.values(colTotals).reduce((a, b) => a + b, 0);
  const activeCols = COLS.filter(c => colTotals[c] > 0);
  if (scRows.length === 0 || grandTotal === 0) {
    return <tr data-id="element-408" data-mp="3tuq3gonr89m5">
        <td colSpan={2} style={{
        padding: '12px 10px',
        color: '#8892a0',
        fontSize: 10,
        fontStyle: 'italic'
      }} data-id="element-409" data-mp="3tu7viurqc6ye">
          No in-progress deals match the current filter.
        </td>
      </tr>;
  }
  return <>
      <thead data-id="element-410" data-mp="2gzbyb1v8xxij">
        <tr data-id="element-411" data-mp="2by4rp64ub4dg">
          <th data-id="element-412" data-mp="2byehaslz39im">SC</th>
          {activeCols.map(c => <th key={c} data-id="element-413" data-mp="2bxcy20uszgpt">{c}</th>)}
          <th data-id="element-414" data-mp="2c0ikf8c1s4hu">Total</th>
        </tr>
      </thead>
      <tbody data-id="element-415" data-mp="2pww2aikhu7wu">
        {scRows.map(row => <tr key={row.full} data-id="element-416" data-mp="2pvt9u67ivxet">
            <td title={row.full} data-id="element-417" data-mp="2lvsn7ya9sfwo">{row.name}</td>
            {activeCols.map(c => <td key={c} data-id="element-418" data-mp="1y6rc23a1yqnp">
                {(row.bkts[c] || 0) > 0 ? <span className="mx-val" data-id="element-419" data-mp="1y6zqw0pqk61j">{row.bkts[c]}</span> : <span style={{
            color: '#d1d5db'
          }} data-id="element-420" data-mp="1t8pq9al8sd8j">—</span>}
              </td>)}
            <td data-id="element-421" data-mp="26zbfri57blfe"><span className="mx-val" style={{
            background: '#e0e7ff',
            color: '#3730a3'
          }} data-id="element-422" data-mp="27038ml7ez51z">{row.total}</span></td>
          </tr>)}
      </tbody>
      <tfoot data-id="element-423" data-mp="226i8onm9g105">
        <tr data-id="element-424" data-mp="1eiqt9b5fb7un">
          <td data-id="element-425" data-mp="1ei8pzukcftd8">Total</td>
          {activeCols.map(c => <td key={c} data-id="element-426" data-mp="1egnwm89rmggy"><span style={{
            fontWeight: 700,
            color: '#1d4ed8'
          }} data-id="element-427" data-mp="1ege6mkoq09b9">{colTotals[c] || 0}</span></td>)}
          <td data-id="element-428" data-mp="1n6eeviwgqtch"><span style={{
            fontWeight: 700,
            color: '#3730a3'
          }} data-id="element-429" data-mp="1n6egfnc431el">{grandTotal}</span></td>
        </tr>
      </tfoot>
    </>;
}

/* ─── Touchpoint expanded view ──────────────────────────────────────────── */

function TouchpointExpandedView({
  touchpoints,
  mode
}: {
  touchpoints: Touchpoint[];
  mode: DealViewMode;
}) {
  const [tSearchId, setTSearchId] = useState('');
  const [tSearchName, setTSearchName] = useState('');
  const [tSearchAccount, setTSearchAccount] = useState('');
  const [tSearchSc, setTSearchSc] = useState('');
  const [tSearchComments, setTSearchComments] = useState('');
  const [tTypeFilter, setTypeFilter] = useState<string[]>([]);
  const [tTypeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [tEntryFrom, setTEntryFrom] = useState('');
  const [tEntryTo, setTEntryTo] = useState('');
  const [tCloseQFilter, setTCloseQFilter] = useState<string[]>([]);
  const [tCloseQDropdownOpen, setTCloseQDropdownOpen] = useState(false);
  const [tSubFilter, setTSubFilter] = useState<string[]>([]);
  const [tSubDropdownOpen, setTSubDropdownOpen] = useState(false);
  const tTypes = [...new Set(touchpoints.map(t => t.touchpointType).filter(Boolean))].sort();
  const tCloseQs = [...new Set(touchpoints.map(t => t.close_q).filter(Boolean))].sort();
  const tSubs = [...new Set(touchpoints.map(t => t.sub_region).filter(Boolean))].sort();
  const parseEntry = (entry: string): Date | null => {
    if (!entry) return null;
    const p = entry.split('/');
    if (p.length !== 3) return null;
    const [dd, mm, yy] = p.map(Number);
    return new Date(yy > 50 ? 1900 + yy : 2000 + yy, mm - 1, dd);
  };
  const filtered = touchpoints.filter(t => {
    if (tSearchId && !t.id.toLowerCase().includes(tSearchId.toLowerCase())) return false;
    if (tSearchName && !t.name.toLowerCase().includes(tSearchName.toLowerCase())) return false;
    if (tSearchAccount && !t.account.toLowerCase().includes(tSearchAccount.toLowerCase())) return false;
    if (tSearchSc && !t.sc.toLowerCase().includes(tSearchSc.toLowerCase())) return false;
    if (tSearchComments && !(t.solutionComments || '').toLowerCase().includes(tSearchComments.toLowerCase())) return false;
    if (tTypeFilter.length > 0 && !tTypeFilter.includes(t.touchpointType)) return false;
    if (tEntryFrom || tEntryTo) {
      const dt = parseEntry(t.entry);
      if (dt) {
        if (tEntryFrom) {
          const f = new Date(tEntryFrom);
          if (dt < f) return false;
        }
        if (tEntryTo) {
          const te = new Date(tEntryTo);
          if (dt > te) return false;
        }
      }
    }
    if (tCloseQFilter.length > 0 && !tCloseQFilter.includes(t.close_q)) return false;
    if (tSubFilter.length > 0 && !tSubFilter.includes(t.sub_region)) return false;
    return true;
  });
  if (touchpoints.length === 0) {
    return <div style={{
      fontSize: 10,
      color: '#8892a0',
      padding: '12px 4px',
      textAlign: 'center'
    }} data-id="element-430" data-mp="1bxb1af0dx5ri">No touchpoints for this SC</div>;
  }
  const inputStyle = {
    fontSize: 10,
    padding: '4px 8px',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    outline: 'none',
    width: 110,
    color: '#344054',
    background: '#fff'
  };
  const dateStyle = {
    fontSize: 10,
    padding: '3px 6px',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    outline: 'none',
    color: '#344054',
    background: '#fff'
  };
  const btnStyle = {
    fontSize: 10,
    padding: '4px 8px',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    outline: 'none',
    color: '#344054',
    background: '#fff',
    cursor: 'pointer' as const,
    display: 'flex' as const,
    alignItems: 'center' as const,
    gap: 4
  };
  const ddStyle = {
    position: 'absolute' as const,
    top: '100%',
    left: 0,
    marginTop: 4,
    zIndex: 50,
    background: '#fff',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    boxShadow: '0 2px 6px rgba(0,0,0,.12)',
    minWidth: 140,
    padding: '4px 0'
  };
  const renderDropdown = (label: string, items: string[], selected: string[], toggle: (v: string[]) => void, open: boolean, setOpen: (v: boolean) => void) => <div style={{
    position: 'relative'
  }} data-id="element-431" data-mp="1g36jqzkanda5">
      <button style={btnStyle} onClick={() => setOpen(!open)} data-id="element-432" data-mp="1g4hjmoamc0vz">
        {selected.length > 0 ? `${label} (${selected.length})` : `${label} ▾`}
      </button>
      {open && <div style={ddStyle} data-id="element-433" data-mp="5pfav50myw2q">
          <div onClick={() => toggle(selected.length === items.length ? [] : [...items])} style={{
        padding: '4px 10px',
        fontSize: 10,
        cursor: 'pointer',
        color: '#667085',
        borderBottom: '1px solid #eaecf0'
      }} data-id="element-434" data-mp="5odpo7pwot7a">
            {selected.length === items.length ? 'Deselect all' : 'Select all'}
          </div>
          {items.map(item => <label key={item} style={{
        padding: '4px 10px',
        fontSize: 10,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 6
      }} onClick={e => e.stopPropagation()} data-id="element-435" data-mp="3j4lhcdh4xuql">
              <input type="checkbox" checked={selected.includes(item)} onChange={() => toggle(selected.includes(item) ? selected.filter(x => x !== item) : [...selected, item])} data-id="element-436" data-mp="3suo8e84p4yma" />
              {item}
            </label>)}
        </div>}
    </div>;
  const toComparable = (entry: string) => {
    if (!entry) return 0;
    const [dd, mm, yy] = entry.split('/');
    return +yy * 10000 + +mm * 100 + +dd;
  };
  const sorted = [...filtered].sort((a, b) => toComparable(b.entry) - toComparable(a.entry));
  const filterBar = <div style={{
    padding: '6px 0',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap'
  }} data-id="element-437" data-mp="18gpz3wxnrm3n">
      <input type="text" placeholder="ID" value={tSearchId} onChange={e => setTSearchId(e.target.value)} style={inputStyle} data-id="element-438" data-mp="13j81h2p3ctay" />
      <input type="text" placeholder="Name" value={tSearchName} onChange={e => setTSearchName(e.target.value)} style={inputStyle} data-id="element-439" data-mp="ttohftpn6tei" />
      <input type="text" placeholder="Account" value={tSearchAccount} onChange={e => setTSearchAccount(e.target.value)} style={inputStyle} data-id="element-440" data-mp="pr9zrcxplzot" />
      <input type="text" placeholder="SC" value={tSearchSc} onChange={e => setTSearchSc(e.target.value)} style={inputStyle} data-id="element-441" data-mp="kut6637qqt4c" />
      <input type="text" placeholder="Comments" value={tSearchComments} onChange={e => setTSearchComments(e.target.value)} style={{
      ...inputStyle,
      width: 120
    }} data-id="element-442" data-mp="ausk49ihlywg" />
      {renderDropdown('Type', tTypes, tTypeFilter, setTypeFilter, tTypeDropdownOpen, setTypeDropdownOpen)}
      <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 3,
      fontSize: 10,
      color: '#667085'
    }} data-id="element-443" data-mp="3bwild5ftroui">
        <span data-id="element-444" data-mp="3pugvtzr0z14q">Entry</span>
        <input type="date" value={tEntryFrom} onChange={e => setTEntryFrom(e.target.value)} style={dateStyle} data-id="element-445" data-mp="3kxgmnfyvpi9e" />
        <span data-id="element-446" data-mp="2x7e01fdqh4i6">–</span>
        <input type="date" value={tEntryTo} onChange={e => setTEntryTo(e.target.value)} style={dateStyle} data-id="element-447" data-mp="2x8xfx4vh9djf" />
      </div>
      {renderDropdown('Close Q', tCloseQs, tCloseQFilter, setTCloseQFilter, tCloseQDropdownOpen, setTCloseQDropdownOpen)}
      {renderDropdown('Sub', tSubs, tSubFilter, setTSubFilter, tSubDropdownOpen, setTSubDropdownOpen)}
    </div>;
  return <>
      {filterBar}
      <div style={{
      fontSize: 9,
      color: '#8892a0',
      marginBottom: 4
    }} data-id="element-448" data-mp="2egcs41umxymh">{sorted.length} of {touchpoints.length} touchpoints</div>
      <TouchpointExpandedViewInner touchpoints={sorted} mode={mode} data-id="element-449" data-mp="29i1hhowink37" />
    </>;
}
function TouchpointExpandedViewInner({
  touchpoints,
  mode
}: {
  touchpoints: Touchpoint[];
  mode: DealViewMode;
}) {
  switch (mode) {
    case 'table':
      return <TouchpointTableView touchpoints={touchpoints} data-id="element-450" data-mp="fzugoen1tvfy" />;
    case 'tile':
      return <TouchpointTileView touchpoints={touchpoints} data-id="element-451" data-mp="fxrgj1s6mlxi" />;
    case 'gallery':
      return <TouchpointGalleryView touchpoints={touchpoints} data-id="element-452" data-mp="kyxb755ekj9o" />;
    case 'waterfall':
      return <TouchpointWaterfallView touchpoints={touchpoints} data-id="element-453" data-mp="18pkyaxnw3fgt" />;
    case 'cards':
    default:
      return <div className="deal-grid" data-id="element-454" data-mp="1cpbiujmtozcw">
          {touchpoints.map(t => <div key={t.task_id} className="deal-chip" title={`${t.account}\n${t.name}\nType: ${t.touchpointType || '—'}\nEntry: ${t.entry}`} data-id="element-455" data-mp="yqvbqrl1t6u8">
              <div style={{
            fontSize: 9,
            color: '#8892a0'
          }} data-id="element-456" data-mp="13r9nokprt0xj">{t.id}</div>
              <div style={{
            fontSize: 10,
            fontWeight: 600,
            marginTop: 2
          }} data-id="element-457" data-mp="3jfjh34kdwe7b">{t.account.substring(0, 16)}</div>
              <div style={{
            fontSize: 9,
            marginTop: 2
          }} data-id="element-458" data-mp="3ogyzspz3a8mj">
                <span title={t.name} data-id="element-459" data-mp="34dt1a1lw331f">{t.name.substring(0, 20)}{t.name.length > 20 ? '…' : ''}</span>
              </div>
              <div style={{
            display: 'flex',
            gap: 4,
            marginTop: 4,
            flexWrap: 'wrap'
          }} data-id="element-460" data-mp="30cr2tmzsqv0i">
                {t.touchpointType && <span className="badge b-blue" style={{
              fontSize: 8
            }} data-id="element-461" data-mp="2vjog9ytfsltt">{t.touchpointType}</span>}
                <span style={{
              fontSize: 8,
              color: '#667085'
            }} data-id="element-462" data-mp="2ql3evw2g7w3s">{t.entry}</span>
              </div>
              <div style={{
            fontSize: 8,
            color: '#667085',
            marginTop: 2
          }} data-id="element-463" data-mp="2lnl5k4kirev9">{t.sub_region} · {t.close_q}</div>
            </div>)}
        </div>;
  }
}

/* ─── Touchpoint view renderers ──────────────────────────────────────────── */

function TouchpointTableView({
  touchpoints
}: {
  touchpoints: Touchpoint[];
}) {
  const tpColumns: SortableColumn<Touchpoint>[] = [{
    key: 'id',
    label: 'ID',
    width: '10%',
    sortValue: t => t.id,
    render: t => <span style={{
      color: '#8892a0',
      fontSize: 9
    }} data-id="element-464" data-mp="5vx8m95i9mcg">{t.id}</span>
  }, {
    key: 'name',
    label: 'Name',
    width: '16%',
    sortValue: t => t.name,
    render: t => <span title={t.name} data-id="element-465" data-mp="dena7ei2sd17">{t.name.substring(0, 22)}</span>
  }, {
    key: 'account',
    label: 'Account',
    width: '14%',
    sortValue: t => t.account,
    render: t => <span title={t.account} data-id="element-466" data-mp="n4gcfowbvfss">{t.account.substring(0, 18)}</span>
  }, {
    key: 'sc',
    label: 'SC',
    width: '10%',
    sortValue: t => t.sc,
    render: t => <span data-id="element-467" data-mp="s39p5f3sj2rw">{t.sc}</span>
  }, {
    key: 'touchpointType',
    label: 'Type',
    width: '12%',
    sortValue: t => t.touchpointType,
    render: t => t.touchpointType ? <span className="badge b-blue" style={{
      fontSize: 8
    }} data-id="element-468" data-mp="3vk9sy3qnsr9u">{t.touchpointType}</span> : <span data-id="element-469" data-mp="4ei73gjx00su">—</span>
  }, {
    key: 'entry',
    label: 'Entry',
    width: '10%',
    sortValue: t => t.entry,
    render: t => <span style={{
      fontSize: 9,
      color: '#667085'
    }} data-id="element-470" data-mp="8glhe1uukzcj">{t.entry}</span>
  }, {
    key: 'solutionComments',
    label: 'Comments',
    width: '18%',
    sortValue: t => t.solutionComments,
    render: t => <span title={t.solutionComments} data-id="element-471" data-mp="1jv8rqkbft3ti">{t.solutionComments ? t.solutionComments.substring(0, 30) + (t.solutionComments.length > 30 ? '…' : '') : '—'}</span>
  }];
  return <SortableTable columns={tpColumns} data={touchpoints} rowKey={t => t.task_id} data-id="element-472" data-mp="1iebrx3va2op7" />;
}
function TouchpointTileView({
  touchpoints
}: {
  touchpoints: Touchpoint[];
}) {
  return <div className="deal-tile-grid" data-id="element-473" data-mp="1sd11m9d0t2ud">
      {touchpoints.map(t => <div key={t.task_id} className="deal-tile" title={`${t.account}\n${t.name}\nType: ${t.touchpointType || '—'}\nEntry: ${t.entry}`} data-id="element-474" data-mp="1ncn1ddhnux6w">
          <div className="dt-id" data-id="element-475" data-mp="21bd170vv7ayy">{t.id}</div>
          <div className="dt-acct" data-id="element-476" data-mp="1we4fiqbicosy">{t.account.substring(0, 14)}</div>
          <div style={{
        fontSize: 8,
        marginTop: 2,
        lineHeight: 1.2
      }} data-id="element-477" data-mp="1wbs12n4ykwkn">
            <span title={t.name} data-id="element-478" data-mp="2b1fruufikcu4">{t.name.substring(0, 16)}</span>
          </div>
          <div style={{
        fontSize: 8,
        color: '#667085',
        marginTop: 2
      }} data-id="element-479" data-mp="263e96am02fj6">{t.entry}</div>
          <span className="badge b-blue" style={{
        fontSize: 8
      }} data-id="element-480" data-mp="2k2xeywrc9esb">{t.touchpointType || '—'}</span>
        </div>)}
    </div>;
}
function TouchpointGalleryView({
  touchpoints
}: {
  touchpoints: Touchpoint[];
}) {
  return <div className="deal-gallery" data-id="element-481" data-mp="2yrflwg412g7r">
      {touchpoints.map(t => <div key={t.task_id} className="deal-gallery-card" data-id="element-482" data-mp="33oz2hmo7752v">
          <div className="dg-id" data-id="element-483" data-mp="33n4oksbcgd8a">{t.id}</div>
          <div className="dg-title" data-id="element-484" data-mp="2ppzbi8d7qe3j">{t.account}</div>
          <div style={{
        fontSize: 10,
        marginBottom: 4
      }} data-id="element-485" data-mp="2prk5nwvm7v0v">
            <span title={t.name} data-id="element-486" data-mp="2unhlm22yuzd4">{t.name.substring(0, 30)}{t.name.length > 30 ? '…' : ''}</span>
          </div>
          <div style={{
        fontSize: 9,
        color: '#667085',
        marginBottom: 4
      }} data-id="element-487" data-mp="2fyuw57jmkr86">{t.entry}</div>
          <div style={{
        fontSize: 9,
        color: '#344054',
        marginBottom: 4,
        lineHeight: 1.3
      }} data-id="element-488" data-mp="2krek8yfxv4r8">
            <span title={t.solutionComments} data-id="element-489" data-mp="26zrdu5sa8fuq">{t.solutionComments ? t.solutionComments.substring(0, 50) + (t.solutionComments.length > 50 ? '…' : '') : '—'}</span>
          </div>
          <div className="dg-meta" data-id="element-490" data-mp="1x125p4q6u9ro">
            {t.touchpointType && <span className="badge b-blue" data-id="element-491" data-mp="1x1k86j3g1k81">{t.touchpointType}</span>}
            <span className="badge b-gray" data-id="element-492" data-mp="21ytwtwj0drut">{t.close_q}</span>
          </div>
          <div className="dg-footer" data-id="element-493" data-mp="7k6kd75vrpyw">
            {t.sub_region} · {t.sc}
          </div>
        </div>)}
    </div>;
}
const TP_TYPE_BUCKETS = ['Internal Alignment', 'Customer Facing', 'No Type'];
function TouchpointWaterfallView({
  touchpoints
}: {
  touchpoints: Touchpoint[];
}) {
  const groups: Record<string, Touchpoint[]> = {};
  for (const b of TP_TYPE_BUCKETS) groups[b] = [];
  for (const t of touchpoints) {
    const bucket = t.touchpointType || 'No Type';
    if (!groups[bucket]) groups[bucket] = [];
    groups[bucket].push(t);
  }
  return <div className="deal-waterfall" data-id="element-494" data-mp="met6w8zogsc3">
      {TP_TYPE_BUCKETS.map(bucket => {
      const items = groups[bucket] || [];
      return <div key={bucket} className="deal-waterfall-lane" data-id="element-495" data-mp="cjj6fpoxgt0f">
            <div className="deal-waterfall-lane-header" data-id="element-496" data-mp="cl407d3f47x8">
              <span data-id="element-497" data-mp="328uy4b1jrt1e">{bucket}</span>
              <span className="lane-count" data-id="element-498" data-mp="325z24s99fefl">{items.length}</span>
            </div>
            <div className="deal-waterfall-lane-body" data-id="element-499" data-mp="2x7np68no4nt5">
              {items.length === 0 && <div style={{
            fontSize: 9,
            color: '#d1d5db',
            textAlign: 'center',
            padding: '12px 4px',
            fontStyle: 'italic'
          }} data-id="element-500" data-mp="2n6lawlb13bvq">none</div>}
              {items.map(t => <div key={t.task_id} className="deal-waterfall-card" data-id="element-501" data-mp="2x2g2i5x8chan">
                  <div className="dw-id" data-id="element-502" data-mp="31ug9ujfmqch9">{t.id}</div>
                  <div className="dw-acct" title={t.account} data-id="element-503" data-mp="31w12g3idvlch">{t.account.substring(0, 18)}</div>
                  <div style={{
              fontSize: 8,
              marginTop: 2,
              lineHeight: 1.2
            }} data-id="element-504" data-mp="35v9xrct2nra5">
                    <span title={t.name} data-id="element-505" data-mp="3atbbrjbn50er">{t.name.substring(0, 20)}</span>
                  </div>
                  <div style={{
              fontSize: 8,
              color: '#667085',
              marginTop: 2
            }} data-id="element-506" data-mp="3fv0pj6q8zs6j">{t.entry}</div>
                  <div className="dw-tags" data-id="element-507" data-mp="3krr2hl6ra7nq">
                    <span className="badge b-blue" style={{
                fontSize: 8
              }} data-id="element-508" data-mp="3kqg1ft4p3e0b">{t.touchpointType || '—'}</span>
                  </div>
                </div>)}
            </div>
          </div>;
    })}
    </div>;
}

/* ─── Main panel ───────────────────────────────────────────────────────── */

export function ODPanel({
  state
}: Props) {
  const [expandedSC, setExpandedSC] = useState<string | null>(null);
  const [dealView, setDealView] = useState<DealViewMode>('cards');
  const [expandedViewMode, setExpandedViewMode] = useState<'deals' | 'touchpoints'>('deals');
  const [scSearch, setScSearch] = useState('');
  const {
    deals,
    touchpoints
  } = useDeals();

  // Touchpoints filters
  const [tpSearchId, setTpSearchId] = useState('');
  const [tpSearchName, setTpSearchName] = useState('');
  const [tpSearchAccount, setTpSearchAccount] = useState('');
  const [tpSearchSc, setTpSearchSc] = useState('');
  const [tpTypeFilter, setTpTypeFilter] = useState<string[]>([]);
  const [tpTypeDropdownOpen, setTpTypeDropdownOpen] = useState(false);
  const [tpEntryFrom, setTpEntryFrom] = useState('');
  const [tpEntryTo, setTpEntryTo] = useState('');
  const tpTypes = [...new Set(touchpoints.map(t => t.touchpointType).filter(Boolean))].sort();
  const parseEntryDate = (entry: string): Date | null => {
    if (!entry) return null;
    const parts = entry.split('/');
    if (parts.length !== 3) return null;
    const [dd, mm, yy] = parts.map(Number);
    const year = yy > 50 ? 1900 + yy : 2000 + yy;
    return new Date(year, mm - 1, dd);
  };
  const touchpointsFiltered = touchpoints.filter(t => {
    if (tpSearchId && !t.id.toLowerCase().includes(tpSearchId.toLowerCase())) return false;
    if (tpSearchName && !t.name.toLowerCase().includes(tpSearchName.toLowerCase())) return false;
    if (tpSearchAccount && !t.account.toLowerCase().includes(tpSearchAccount.toLowerCase())) return false;
    if (tpSearchSc && !t.sc.toLowerCase().includes(tpSearchSc.toLowerCase())) return false;
    if (tpTypeFilter.length > 0 && !tpTypeFilter.includes(t.touchpointType)) return false;
    if (tpEntryFrom || tpEntryTo) {
      const d = parseEntryDate(t.entry);
      if (d) {
        if (tpEntryFrom) {
          const from = parseEntryDate(tpEntryFrom);
          if (from && d < from) return false;
        }
        if (tpEntryTo) {
          const to = parseEntryDate(tpEntryTo);
          if (to && d > to) return false;
        }
      }
    }
    return true;
  });
  const scDataAll = buildSCList(state.r, state.sub, state, deals);
  const tpSCDataAll = buildTouchpointSCList(state.r, state.sub, state, touchpoints);
  const tpSCMap = new Map(tpSCDataAll.map(tp => [tp.n, tp]));
  if (touchpoints.length > 0 && tpSCDataAll.length <= 2) {
    const scSamples = touchpoints.slice(0, 5).map(t => t.sc);
    console.log('[ODPanel] touchpoint sc samples:', scSamples);
    console.log('[ODPanel] unique sc values:', new Set(touchpoints.map(t => t.sc)).size);
  }
  const scFiltered = scSearch ? scDataAll.filter(s => s.n.toLowerCase().includes(scSearch.toLowerCase())) : scDataAll;
  const allIP = deals.filter(d => {
    if (d.status !== 'In Progress') return false;
    if (state.r !== 'Global' && d.region !== state.r) return false;
    if (state.sub && state.sub !== 'All' && d.sub_region !== state.sub) return false;
    if (!matchesCQFilter(d, state)) return false;
    return true;
  });
  const scs = new Set(allIP.flatMap(d => splitSCNames(d.sc))).size;

  // oppGnarr includes ALL statuses (not just IP), matching region/sub/CQ
  const allForGnarr = deals.filter(d => {
    if (state.r !== 'Global' && d.region !== state.r) return false;
    if (state.sub && state.sub !== 'All' && d.sub_region !== state.sub) return false;
    if (!matchesCQFilter(d, state)) return false;
    return true;
  });
  const oppGnarr = sumUniqueBy(allForGnarr, d => d.opp_gnarr);
  const ages = validAges(allForGnarr);
  const maxIP = Math.max(...scFiltered.map(s => s.ip), 1);
  const maxClosed = Math.max(...scFiltered.map(s => s.closed), 1);
  const scColumns: SortableColumn<SCData>[] = [{
    key: 'n',
    label: 'SC Name',
    width: '22%',
    render: p => <span className="name-link" data-id="element-509" data-mp="1wfy546hhgxfs">
          {p.n}
          <span className={`chev${expandedSC === p.n ? ' open' : ''}`} data-id="element-510" data-mp="1wf6cn4j6nftq">▾</span>
        </span>
  }, {
    key: 'ip',
    label: 'In-progress',
    width: '14%',
    sortValue: p => p.ip,
    render: p => <>
          <div className="pbg" style={{
        width: 70
      }} data-id="element-511" data-mp="25etcvn06efl7">
            <div className="pf" style={{
          width: `${Math.round(p.ip / maxIP * 100)}%`
        }} data-id="element-512" data-mp="2aeo4jsrrz97b" />
          </div>
          <strong data-id="element-513" data-mp="1dqrjaale7ppr">{p.ip}</strong>
        </>
  }, {
    key: 'closed',
    label: 'Closed',
    width: '14%',
    sortValue: p => p.closed,
    render: p => <>
          <div className="pbg" style={{
        width: 70
      }} data-id="element-514" data-mp="1mpwfha9hf6yr">
            <div className="pf" style={{
          width: `${Math.round(p.closed / maxClosed * 100)}%`,
          background: '#34d399'
        }} data-id="element-515" data-mp="1mobnnsejy24l" />
          </div>
          <strong data-id="element-516" data-mp="u20l4c7dxr55">{p.closed}</strong>
        </>
  }, {
    key: 'avgAge',
    label: 'Avg age (Closed)',
    width: '11%',
    sortValue: p => p.avgAge,
    render: p => <span style={{
      color: p.avgAge > 90 ? '#f87171' : p.avgAge > 40 ? '#d4a017' : '#344054',
      fontWeight: p.avgAge > 40 ? 600 : 400
    }} data-id="element-517" data-mp="rgsut3wb1rrk">
          {p.avgAge}d
        </span>
  }, {
    key: 'med',
    label: 'Median age (Closed)',
    width: '11%',
    sortValue: p => p.med,
    render: p => <>{p.med}d</>
  }, {
    key: 'gnarr',
    label: 'GNARR (IP)',
    width: '14%',
    sortValue: p => p.gnarr,
    render: p => <>{fmtG(p.gnarr)}</>
  }, {
    key: 'closedGnarr',
    label: 'GNARR (Closed)',
    width: '14%',
    sortValue: p => p.closedGnarr,
    render: p => <>{fmtG(p.closedGnarr)}</>
  }, {
    key: 'tpCount',
    label: 'TP',
    width: '6%',
    sortValue: p => tpSCMap.get(p.n)?.count ?? 0,
    render: p => {
      const count = tpSCMap.get(p.n)?.count ?? 0;
      return <span style={{
        color: count > 0 ? '#3B82F6' : '#8892a0',
        fontWeight: count > 0 ? 600 : 400
      }} data-id="element-518" data-mp="3lxcwoni2wh6r">{count}</span>;
    }
  }];
  return <div data-id="element-519" data-mp="3h03bjk1q2roq">
      {/* Metrics */}
      <div className="mrow" data-id="element-520" data-mp="1kv40ctf2upc7">
        <MetricCard label="In-progress requests" value={allIP.length} sub={`${scs} SCs active`} colorClass="c-blue" data-id="element-521" data-mp="1kvwyx73vu8j9" />
        <MetricCard label="Requests / person" value={scFiltered.length ? +(allIP.length / scFiltered.length).toFixed(1) : 0} sub="across active SCs" colorClass="c-purple" data-id="element-522" data-mp="1ovxo9msis453" />
        <MedianAgeCard ages={ages} data-id="element-523" data-mp="1g3c90go3kfl0" />
        <GnarrInfluencedCard value={oppGnarr} colorClass="c-green" data-id="element-524" data-mp="1g2awe7rfnl16" />
      </div>

      {/* SC workload table */}
      <SectionHeader title="SC workload — requests / touchpoints (click row to expand)" data-id="element-525" data-mp="1197n0nnzwdlj" />
      <div className="tw" data-id="element-526" data-mp="163v1dtpabrme">
        <div style={{
        padding: '8px 12px 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }} data-id="element-527" data-mp="s6g82dvwuzp5">
          <input type="text" placeholder="Search SC name…" value={scSearch} onChange={e => {
          setScSearch(e.target.value);
          setExpandedSC(null);
        }} style={{
          fontSize: 10,
          padding: '4px 8px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          width: 160,
          color: '#344054',
          background: '#fff'
        }} data-id="element-528" data-mp="x4hr4ytc6z0m" />
        </div>
        <SortableTable columns={scColumns} data={scFiltered} rowKey={p => p.n} defaultSort={{
        key: 'ip',
        dir: 'desc'
      }} onRowClick={p => {
        if (expandedSC === p.n) {
          setExpandedSC(null);
        } else {
          setExpandedSC(p.n);
          setExpandedViewMode('deals');
        }
      }} renderExpandedRow={p => expandedSC === p.n ? <tr key={`exp-${p.n}`} data-id="element-530" data-mp="3te58b05k7cwy">
              <td colSpan={8} style={{
          padding: 0
        }} data-id="element-531" data-mp="3td3vor8waid4">
                <div className="exp-inner" data-id="element-532" data-mp="3pctojb9d9lwo">
                  <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 6
            }} data-id="element-533" data-mp="3pe4v1iu757rh">
                    <div style={{
                fontSize: 9,
                fontWeight: 700,
                color: '#667085',
                textTransform: 'uppercase',
                letterSpacing: '.05em',
                flex: 1
              }} data-id="element-534" data-mp="1z15lalyungfn">
                      {expandedViewMode === 'deals' ? <>In-progress book — {p.n} ({p.ip} deals · {fmtG(p.gnarr)} GNARR)</> : <>Touchpoints — {p.n} ({tpSCMap.get(p.n)?.count ?? 0} touchpoints)</>}
                    </div>
                    <div style={{
                display: 'flex',
                border: '1px solid #d0d5dd',
                borderRadius: 5,
                overflow: 'hidden'
              }} data-id="element-535" data-mp="7yoxcrh5n9gq">
                      <button onClick={() => setExpandedViewMode('deals')} style={{
                  fontSize: 9,
                  padding: '3px 10px',
                  border: 'none',
                  cursor: 'pointer',
                  background: expandedViewMode === 'deals' ? '#3B82F6' : '#fff',
                  color: expandedViewMode === 'deals' ? '#fff' : '#344054',
                  fontWeight: expandedViewMode === 'deals' ? 600 : 400
                }} data-id="element-536" data-mp="gxa9o4w19s92">
                        Requests
                      </button>
                      <button onClick={() => setExpandedViewMode('touchpoints')} style={{
                  fontSize: 9,
                  padding: '3px 10px',
                  border: 'none',
                  borderLeft: '1px solid #d0d5dd',
                  cursor: 'pointer',
                  background: expandedViewMode === 'touchpoints' ? '#3B82F6' : '#fff',
                  color: expandedViewMode === 'touchpoints' ? '#fff' : '#344054',
                  fontWeight: expandedViewMode === 'touchpoints' ? 600 : 400
                }} data-id="element-537" data-mp="2p4du89gx6icr">
                        Touchpoints
                      </button>
                    </div>
                    <ViewSwitcher active={dealView} onChange={setDealView} data-id="element-538" data-mp="2z0gtn7ke02w0" />
                  </div>
                  {expandedViewMode === 'deals' ? <>
                      <DealExpandedView deals={p.deals} mode={dealView} data-id="element-539" data-mp="3rkfdg8dz1zhc" />
                      {p.closedDeals.length > 0 && <>
                          <div style={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: '#059669',
                  margin: '12px 0 6px',
                  textTransform: 'uppercase',
                  letterSpacing: '.05em'
                }} data-id="element-540" data-mp="2v4kvxuoemsua">
                            Closed — {p.n} ({p.closed} deals)
                          </div>
                          <DealExpandedView deals={p.closedDeals} mode={dealView} data-id="element-541" data-mp="2m2bm770yn3hy" />
                        </>}
                    </> : <TouchpointExpandedView touchpoints={tpSCMap.get(p.n)?.touchpoints ?? []} mode={dealView} data-id="element-542" data-mp="3dw3xry6p00uq" />}
                </div>
              </td>
            </tr> : null} data-id="element-529" data-mp="2wppgsyiw2th7" />
      </div>

      {/* Solution × SC matrix */}
      <SectionHeader title="Solution × SC matrix — product concentration" data-id="element-543" data-mp="39sxvt2qm4vu2" />
      <div className="tw" data-id="element-544" data-mp="1tfh7yruql0cx">
        <div className="sub" data-id="element-545" data-mp="1tf7m9ggq5fd1">
          In-progress deals by product bucket per SC. Reveals single-threading and coverage gaps.
        </div>
        <div style={{
        overflowX: 'auto'
      }} data-id="element-546" data-mp="1ychaissdnkza">
          <table className="tbl" id="od-matrix-tbl" style={{
          minWidth: 500
        }} data-id="element-547" data-mp="1px0egaply4t4">
            <SCMatrix region={state.r} sub={state.sub} cqState={state} data={deals} data-id="element-548" data-mp="1lvychm4b3eng" />
          </table>
        </div>
      </div>

      {/* Touchpoints — deal reviews */}
      <SectionHeader title="Touchpoints — deal reviews" data-id="element-549" data-mp="1zuy14trtjtjm" />
      <div style={{
      padding: '8px 18px 0',
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      flexWrap: 'wrap'
    }} data-id="element-550" data-mp="1uwd39101hlyc">
        <input type="text" placeholder="Search ID…" value={tpSearchId} onChange={e => setTpSearchId(e.target.value)} style={{
        fontSize: 10,
        padding: '4px 8px',
        border: '1px solid #d0d5dd',
        borderRadius: 5,
        outline: 'none',
        width: 110,
        color: '#344054',
        background: '#fff'
      }} data-id="element-551" data-mp="177tukkd2v75q" />
        <input type="text" placeholder="Search name…" value={tpSearchName} onChange={e => setTpSearchName(e.target.value)} style={{
        fontSize: 10,
        padding: '4px 8px',
        border: '1px solid #d0d5dd',
        borderRadius: 5,
        outline: 'none',
        width: 140,
        color: '#344054',
        background: '#fff'
      }} data-id="element-552" data-mp="og10snq6phwy" />
        <input type="text" placeholder="Search account…" value={tpSearchAccount} onChange={e => setTpSearchAccount(e.target.value)} style={{
        fontSize: 10,
        padding: '4px 8px',
        border: '1px solid #d0d5dd',
        borderRadius: 5,
        outline: 'none',
        width: 140,
        color: '#344054',
        background: '#fff'
      }} data-id="element-553" data-mp="38ubgis9tprqf" />
        <input type="text" placeholder="Search SC…" value={tpSearchSc} onChange={e => setTpSearchSc(e.target.value)} style={{
        fontSize: 10,
        padding: '4px 8px',
        border: '1px solid #d0d5dd',
        borderRadius: 5,
        outline: 'none',
        width: 140,
        color: '#344054',
        background: '#fff'
      }} data-id="element-554" data-mp="3rkjaajrmv5ty" />
        <div style={{
        position: 'relative'
      }} data-id="element-555" data-mp="275zjgmyt58t0">
          <button onClick={() => setTpTypeDropdownOpen(!tpTypeDropdownOpen)} style={{
          fontSize: 10,
          padding: '4px 8px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          color: '#344054',
          background: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 4
        }} data-id="element-556" data-mp="moqdb4dw2fbo">
            {tpTypeFilter.length > 0 ? `Type (${tpTypeFilter.length})` : 'Type ▾'}
          </button>
          {tpTypeDropdownOpen && <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: 4,
          zIndex: 50,
          background: '#fff',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          boxShadow: '0 2px 6px rgba(0,0,0,.12)',
          minWidth: 160,
          padding: '4px 0'
        }} data-id="element-557" data-mp="1adt1kv3136f5">
              <div onClick={() => setTpTypeFilter(tpTypeFilter.length === tpTypes.length ? [] : [...tpTypes])} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            color: '#667085',
            borderBottom: '1px solid #eaecf0'
          }} data-id="element-558" data-mp="3g7j1pem49ttq">
                {tpTypeFilter.length === tpTypes.length ? 'Deselect all' : 'Select all'}
              </div>
              {tpTypes.map(t => <label key={t} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }} onClick={e => e.stopPropagation()} data-id="element-559" data-mp="11dmt73c2d2of">
                  <input type="checkbox" checked={tpTypeFilter.includes(t)} onChange={() => {
              setTpTypeFilter(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
            }} data-id="element-560" data-mp="1b8esqcp7hzlu" />
                  {t}
                </label>)}
            </div>}
        </div>
        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 10,
        color: '#667085'
      }} data-id="element-561" data-mp="1p37hm1qzks53">
          <span data-id="element-562" data-mp="28p97uzf8cwjc">Entry</span>
          <input type="date" value={tpEntryFrom} onChange={e => setTpEntryFrom(e.target.value)} style={{
          fontSize: 10,
          padding: '3px 6px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          color: '#344054',
          background: '#fff'
        }} data-id="element-563" data-mp="28lv4poyqsjau" />
          <span data-id="element-564" data-mp="1pwx0fy9pz52c">–</span>
          <input type="date" value={tpEntryTo} onChange={e => setTpEntryTo(e.target.value)} style={{
          fontSize: 10,
          padding: '3px 6px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          color: '#344054',
          background: '#fff'
        }} data-id="element-565" data-mp="1pwok1wee1hme" />
        </div>
      </div>
      <div className="tw" data-id="element-566" data-mp="16yughxw13ybe">
        <SortableTable columns={[{
        key: 'id',
        label: 'ID',
        width: '10%',
        sortValue: (t: Touchpoint) => t.id,
        render: (t: Touchpoint) => <span style={{
          fontSize: 9,
          color: '#8892a0'
        }} data-id="element-568" data-mp="13wpxpr2p7w5j">{t.id}</span>
      }, {
        key: 'name',
        label: 'Touchpoints Name',
        width: '18%',
        sortValue: (t: Touchpoint) => t.name,
        render: (t: Touchpoint) => <span title={t.name} data-id="element-569" data-mp="u6o7asmvi1ng">{t.name.substring(0, 24)}</span>
      }, {
        key: 'account',
        label: 'Account',
        width: '14%',
        sortValue: (t: Touchpoint) => t.account,
        render: (t: Touchpoint) => <span title={t.account} data-id="element-570" data-mp="l7172a5vr1vz">{t.account.substring(0, 18)}</span>
      }, {
        key: 'sc',
        label: 'SC',
        width: '10%',
        sortValue: (t: Touchpoint) => t.sc,
        render: (t: Touchpoint) => <span title={t.sc} data-id="element-571" data-mp="b824987sq0gw">{t.sc}</span>
      }, {
        key: 'touchpointType',
        label: 'Type',
        width: '12%',
        sortValue: (t: Touchpoint) => t.touchpointType,
        render: (t: Touchpoint) => t.touchpointType ? <span className="badge b-blue" style={{
          fontSize: 8
        }} data-id="element-572" data-mp="25mv098clct9d">{t.touchpointType}</span> : <span style={{
          color: '#d1d5db'
        }} data-id="element-573" data-mp="20n8l2td7d2xx">—</span>
      }, {
        key: 'entry',
        label: 'Entry',
        width: '12%',
        sortValue: (t: Touchpoint) => {
          const p = t.entry.split('/');
          if (p.length !== 3) return 0;
          const [dd, mm, yy] = p.map(Number);
          return new Date(yy > 50 ? 1900 + yy : 2000 + yy, mm - 1, dd).getTime();
        },
        render: (t: Touchpoint) => <span style={{
          fontSize: 9,
          color: '#667085'
        }} data-id="element-574" data-mp="2mucelelnhxog">{t.entry}</span>
      }, {
        key: 'solutionComments',
        label: 'Comments',
        width: '16%',
        sortValue: (t: Touchpoint) => t.solutionComments,
        render: (t: Touchpoint) => <span title={t.solutionComments} data-id="element-575" data-mp="1u92r0dymetc0">{t.solutionComments ? t.solutionComments.substring(0, 35) + (t.solutionComments.length > 35 ? '…' : '') : '—'}</span>
      }]} data={touchpointsFiltered} rowKey={(t: Touchpoint) => t.task_id} defaultSort={{
        key: 'entry',
        dir: 'desc'
      }} data-id="element-567" data-mp="16zndi756r9gc" />
      </div>
    </div>;
}